import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  useGetCollectionImport,
  useUsersListNamesForIDs,
} from '@/api/trafikportalen';
import { useGetStudentCollection } from '@/hooks/useGetStudentCollection';
import { useGetAllAdmins } from '@/hooks/useGetAllAdmins';
import { useImportStatusLabels } from '@/hooks/useImportStatusLabels';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { useAscOrDescIcon } from '@/hooks/useSort';
import { createSortConfig } from '@/utils/sort';
import { getImportStateMeta, ImportState } from '@/helpers/import';
import { formatDateTime } from '@/helpers/formatting';
import { Loading } from '@/components/Loading';
import { DataNotFound } from '@/components/DataNotFound';
import Header from '@/components/Header/Header';
import { Section } from '@/components/Section';
import { VirtualizedList } from '@/components/VirtualizedList';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { FilterPanel } from '@/components/FilterPanel';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import Input from '@/components/core/Input';
import Button from '@/components/core/Button';
import { CollectionImportChangeListItem } from './CollectionImportChangeListItem';
import type { CollectionImportChangeDTO } from '@/api/trafikportalen';
import { STUDENT_TYPE_VIEW } from '@/helpers/collectionTypes';
import type { StudentType } from '@/types/students';
import type { FilterType, SearchFilter } from '@/types/filterSearch';
import type { FormattedStudent } from '@/types/students';

interface EnrichedChange {
  change: CollectionImportChangeDTO;
  first_name: string;
  last_name: string;
  national_identity_number: string;
  name: string;
  traveller_id?: string;
}

interface CollectionImportDetailsProps {
  importId: string;
  studentType: StudentType;
  tenantId: string;
  searchParams: SearchFilter;
}

export const CollectionImportDetails = ({
  importId,
  studentType,
  tenantId,
  searchParams,
}: CollectionImportDetailsProps) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const rightPanelRef = useRef<HTMLElement>(null);
  const { query, sort, direction } = searchParams;
  const [announcement, setAnnouncement] = useState('');
  const statusLabels = useImportStatusLabels();
  const getIcon = useAscOrDescIcon();

  const {
    collectionId,
    students,
    isLoading: collectionLoading,
  } = useGetStudentCollection(studentType);

  const { admins, isLoading: adminsLoading } = useGetAllAdmins();

  const { data: importDetails, isLoading: importLoading } =
    useGetCollectionImport(collectionId ?? '', importId, {
      query: {
        enabled: !!collectionId,
      },
    });

  const studentMap = useMemo(() => {
    const map = new Map<string, FormattedStudent>();
    students?.forEach((s) => {
      map.set(s.traveller_id, s);
      if (s.id) {
        map.set(s.id, s);
      }
    });
    return map;
  }, [students]);

  const enrichedChanges = useMemo((): EnrichedChange[] => {
    if (!importDetails?.changes) return [];

    return importDetails.changes
      .filter((change) => change.action !== 'failed')
      .map((change) => {
        const studentByMember = change.collection_member_id
          ? studentMap.get(change.collection_member_id)
          : undefined;
        const studentByTraveller = change.traveller_id
          ? studentMap.get(change.traveller_id)
          : undefined;
        const student = studentByMember ?? studentByTraveller;

        const firstName = student?.first_name ?? '';
        const lastName = student?.last_name ?? '';
        const name = student?.name ?? `${firstName} ${lastName}`.trim();

        return {
          change,
          first_name: firstName,
          last_name: lastName,
          national_identity_number: student?.national_identity_number ?? '',
          name,
          traveller_id: student?.traveller_id ?? change.traveller_id,
        };
      });
  }, [importDetails?.changes, studentMap]);

  const searchKeys = useMemo(
    (): (keyof EnrichedChange)[] => [
      'first_name',
      'last_name',
      'national_identity_number',
      'name',
    ],
    [],
  );

  const sortConfigs = useMemo(
    () =>
      createSortConfig<EnrichedChange>({
        textFields: ['first_name', 'last_name'],
      }),
    [],
  );

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<EnrichedChange>({
    items: enrichedChanges,
    searchKeys,
    sortConfigs,
    routePath: `/${tenantId}/students/${studentType}/imports/${importId}`,
  });

  const statusOptions = useMemo((): OptionsType[] => {
    if (!importDetails?.summary) return [];

    return (Object.entries(importDetails.summary) as [ImportState, number][])
      .filter(([id]) => id !== 'failed')
      .map(([id, count]) => ({
        id,
        label: `${statusLabels[id]} (${count})`,
      }));
  }, [importDetails?.summary, statusLabels]);

  const filteredChanges = useMemo(() => {
    if (!sortedItems) return [];

    const selectedStatuses = filterSearch?.state;
    if (!selectedStatuses || selectedStatuses.length === 0) {
      return sortedItems;
    }

    const selectedIds = selectedStatuses.map((s) => s.id);
    return sortedItems.filter((item) =>
      selectedIds.includes(item.change.action),
    );
  }, [sortedItems, filterSearch?.state]);

  const handleFilter = useCallback(
    (filters: OptionsType[]) => {
      const filterTypes: FilterType[] = filters.map((f) => ({
        id: String(f.id),
        label: String(f.label ?? ''),
      }));
      setFilterSearch((prev) => ({
        ...prev!,
        state: filterTypes,
      }));
      handleSearchParams({
        state: filters.map((f) => String(f.id)),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const adminCreatorName = useMemo(() => {
    if (!importDetails?.created_by) return undefined;
    const admin = admins.find((a) => a.id === importDetails.created_by);
    return admin ? `${admin.firstname} ${admin.lastname}` : undefined;
  }, [importDetails?.created_by, admins]);

  const unknownCreatorId =
    importDetails?.created_by && !adminCreatorName
      ? importDetails.created_by
      : '';

  const { data: fetchedUserNames } = useUsersListNamesForIDs(
    { ids: unknownCreatorId },
    {
      query: {
        enabled: !adminsLoading && unknownCreatorId.length > 0,
      },
    },
  );

  const creatorName = useMemo(() => {
    if (adminCreatorName) return adminCreatorName;
    const fetched = fetchedUserNames?.find(
      (u) => u.id === importDetails?.created_by,
    );
    return fetched ? `${fetched.firstname} ${fetched.lastname}` : undefined;
  }, [adminCreatorName, fetchedUserNames, importDetails?.created_by]);

  const totalChanges = useMemo(() => {
    if (!importDetails?.summary) return 0;
    return Object.values(importDetails.summary).reduce(
      (sum, count) => sum + count,
      0,
    );
  }, [importDetails?.summary]);

  const handleItemClick = useCallback(
    (item: EnrichedChange) => {
      if (!item.traveller_id) return;
      navigate({
        to: '/$tenantId/travellers/$travellerId/$view',
        params: {
          tenantId,
          travellerId: item.traveller_id,
          view: STUDENT_TYPE_VIEW[studentType],
        },
      });
    },
    [navigate, tenantId, studentType],
  );

  const renderChange = useCallback(
    (item: EnrichedChange) => (
      <CollectionImportChangeListItem
        key={`${item.change.row_number}-${item.change.collection_member_id ?? item.change.traveller_id ?? ''}`}
        change={item.change}
        name={item.name}
        nationalIdentityNumber={item.national_identity_number}
        onClick={item.traveller_id ? () => handleItemClick(item) : undefined}
      />
    ),
    [handleItemClick],
  );

  useEffect(() => {
    if (filterSearch || statusOptions.length === 0) return;

    setFilterSearch({
      query: query ?? '',
      state: [],
      sort: sort ?? 'first_name',
      direction: direction ?? 'asc',
    });
  }, [filterSearch, statusOptions, setFilterSearch, query, sort, direction]);

  useEffect(() => {
    if (!filterSearch || !filteredChanges) return;
    const count = filteredChanges.length;
    setAnnouncement(
      formatMessage(
        {
          defaultMessage:
            '{count, plural, one {# ändring} other {# ändringar}} visas',
        },
        { count },
      ),
    );
  }, [filterSearch, filteredChanges, formatMessage]);

  if (collectionLoading || importLoading || adminsLoading) {
    return <Loading />;
  }

  if (!importDetails) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Importdetaljer kunde inte hämtas" />
        }
        buttonTitle={<FormattedMessage defaultMessage="Tillbaka" />}
        onClick={() =>
          navigate({
            to: `/$tenantId/students/${studentType}/$view`,
            params: { tenantId, view: 'imports' },
          })
        }
      />
    );
  }

  return (
    <>
      <Header title={formatMessage({ defaultMessage: 'Importdetaljer' })} />
      <FilteredListContainer className="size-full">
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel ref={rightPanelRef}>
          {filterSearch && (
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label={formatMessage({ defaultMessage: 'Sök' })}
                  placeholder={formatMessage({
                    defaultMessage: 'Sök på namn eller personnummer',
                  })}
                  id="import-detail-search"
                  value={filterSearch.query ?? ''}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              }
              sortingOptions={[
                <Button
                  key="sort-first-name"
                  variant={
                    filterSearch.sort === 'first_name' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'first_name',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('first_name')}
                  aria-pressed={filterSearch.sort === 'first_name'}
                  aria-label={formatMessage(
                    {
                      defaultMessage: 'Sortera efter förnamn {direction}',
                    },
                    {
                      direction:
                        filterSearch.direction === 'asc'
                          ? formatMessage({ defaultMessage: 'stigande' })
                          : formatMessage({ defaultMessage: 'fallande' }),
                    },
                  )}
                >
                  <FormattedMessage defaultMessage="Förnamn" />
                </Button>,
                <Button
                  key="sort-last-name"
                  variant={
                    filterSearch.sort === 'last_name' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'last_name',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('last_name')}
                  aria-pressed={filterSearch.sort === 'last_name'}
                  aria-label={formatMessage(
                    {
                      defaultMessage: 'Sortera efter efternamn {direction}',
                    },
                    {
                      direction:
                        filterSearch.direction === 'asc'
                          ? formatMessage({ defaultMessage: 'stigande' })
                          : formatMessage({ defaultMessage: 'fallande' }),
                    },
                  )}
                >
                  <FormattedMessage defaultMessage="Efternamn" />
                </Button>,
              ]}
              filterOptions={[
                <Dropdown
                  key="status-filter"
                  label={formatMessage({ defaultMessage: 'Status' })}
                  id="import-detail-status"
                  options={statusOptions}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.state ?? []}
                  onValueChange={handleFilter}
                  multiSelect
                  containerRef={rightPanelRef}
                />,
              ]}
            />
          )}
        </RightPanel>
        <div className="flex size-full flex-col md:flex-[4]">
          <div className="px-4 pt-4 md:px-8">
            <Section>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  <div className="flex flex-col">
                    <span className="text-text-700">
                      <FormattedMessage defaultMessage="Datum" />
                    </span>
                    <span className="font-medium text-text-900">
                      {formatDateTime(importDetails.created_at)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-text-700">
                      <FormattedMessage defaultMessage="Skapare" />
                    </span>
                    <span className="font-medium text-text-900">
                      {creatorName ?? (
                        <i>
                          <FormattedMessage defaultMessage="Okänd skapare (behörighet saknas eller borttagen)" />
                        </i>
                      )}
                    </span>
                  </div>
                  {/* TODO: Add strategy back in when backend actually supports more than 'replace' */}
                  {/* <div className="flex flex-col">
                    <span className="text-text-700">
                      <FormattedMessage defaultMessage="Strategi" />
                    </span>
                    <span className="font-medium text-text-900">
                      {strategyLabel}
                    </span>
                  </div> */}
                  <div className="flex flex-col">
                    <span className="text-text-700">
                      <FormattedMessage defaultMessage="Lyckade rader" />
                    </span>
                    <span className="font-medium text-text-900">
                      {totalChanges - importDetails.summary.failed}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1">
                  {(
                    Object.entries(importDetails.summary) as [
                      ImportState,
                      number,
                    ][]
                  ).map(([state, count]) => {
                    const { icon: Icon, textColor } = getImportStateMeta(state);
                    return (
                      <div key={state} className="flex items-center gap-1">
                        <Icon className={`size-4 ${textColor}`} />
                        <span className="text-text-700">
                          {count}{' '}
                          <FormattedMessage
                            id={`import.state.${state}${count === 1 ? '' : 'Plural'}`}
                          />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Section>
          </div>
          {filteredChanges.length === 0 ? (
            <div className="px-4 pt-4 md:px-8">
              <div className="flex w-full items-center rounded-md border border-background-300 bg-background-50 p-4 text-sm md:text-base">
                <span className="italic text-text-800">
                  <FormattedMessage defaultMessage="Inga poster matchar filtreringen" />
                </span>
              </div>
            </div>
          ) : (
            <VirtualizedList
              count={filteredChanges.length}
              countName={formatMessage({ defaultMessage: 'ändringar' })}
              totalItems={enrichedChanges}
              filteredItems={filteredChanges}
              renderItem={renderChange}
              getItemKey={(item) =>
                `${item.change.row_number}-${item.change.collection_member_id ?? item.change.traveller_id ?? ''}`
              }
            />
          )}
        </div>
      </FilteredListContainer>
    </>
  );
};
